import { get, isArray } from 'lodash';

import BackendConnector from './backendConnector';
import CandidateProfileHttpService from './CandidateProfileHttpService';
import EmployerWorkflowStatus from '../constants/EmployerWorkflowStatus';

export default class EmployerHttpService {
    static getEmployer(employerId) {
        return BackendConnector.get(`employer/${employerId}`);
    }

    static getEmployersAvailableForEmployerRep() {
        return BackendConnector.get('/employer/getEmployersAvailableForEmployerRep');
    }

    static getReseller(employerId) {
        return BackendConnector.get(`employer/${employerId}/reseller`);
    }

    static getResellerEmployers({ resellerEmployerId, employerIds }) {
        return BackendConnector.post(`employer/${resellerEmployerId}/resellers`, { employerIds });
    }

    static getEmployersByIds(ids) {
        if (isArray(ids) && ids.length) {
            return BackendConnector.post('employer/search', { employerIDs: ids });
        }
        return [];
    }

    static searchEmployers(filter) {
        return BackendConnector.post('employer/search', filter);
    }

    static getAllEmployers(projection = []) {
        return BackendConnector.post('employers', { projection });
    }

    static getAllEmployersForMenu() {
        return BackendConnector.post('employers', { projection: ['name', 'office', 'status.current', 'settings.isResellerPartner'] });
    }

    static getEmployersByPositionIds(positionIDs) {
        return BackendConnector.post('employer/getByPositionIds', { positionIDs });
    }

    static activateEmployer(employerId) {
        return BackendConnector.put(`employer/${employerId}/activate`);
    }

    static deactivateEmployer(employerId) {
        return BackendConnector.put(`employer/${employerId}/deactivate`);
    }

    static getEmployerWallet(employerId) {
        return BackendConnector.get(`employer/${employerId}/wallet`);
    }

    static async getFeedbackCandidates({
        employerId,
        positionId,
        isListView,
        pageOptions,
        filter,
        includeSamples,
        expectedTalentIds = null,
    }) {
        const {
            list: feedbackCandidates,
            total,
            counters,
            talentIds,
        } = await BackendConnector.post(
            `employer/${employerId}/position/${positionId}/workflowStep/${EmployerWorkflowStatus.FEEDBACK}/candidates`,
            {
                isListView,
                pageOptions,
                filter,
                includeSamples,
                expectedTalentIds,
            },
        );

        const list = (feedbackCandidates || []).map(
            (candidate) => CandidateProfileHttpService.getPreparedCandidate(candidate, candidate),
        );

        return {
            list,
            total,
            counters,
            talentIds,
        };
    }

    static getEmployerWorkflowCounters(employerId, data) {
        return BackendConnector.post(`employer/${employerId}/workflowCounters`, data);
    }

    static getEmployerActiveServicePlanOptions(employerId) {
        return BackendConnector.get(`employer/${employerId}/activeServicePlanOptions`);
    }

    static getEmployerDiversityReportData(employerId, filters) {
        return BackendConnector.post(`employer/${employerId}/diversityReport`, { filters });
    }

    static getEmployerDiversityTypes(employerId) {
        return BackendConnector.get(`employer/${employerId}/diversityTypes`);
    }

    static getSystemCampaignMessagesForEmployer(employerId) {
        return BackendConnector.get(`employer/${employerId}/campaign/systemMessages`);
    }

    static getPublicDiversityReportData(employerId, filters) {
        return BackendConnector.post(`employer/${employerId}/publicDiversityReport`, { filters });
    }

    static getAtsRecruiters(employerId) {
        return BackendConnector.get(`employer/${employerId}/atsRecruiters`);
    }

    static async searchCandidates({ employerId, params, projection }) {
        const response = await BackendConnector.post(
            `employer/${employerId}/search/candidates`,
            { params, projection },
        );

        const taskId = get(response, 'taskId');

        if (!taskId) {
            return undefined;
        }

        const result = await BackendConnector.pollUntilTaskFinished(taskId);
        return result;
    }

    static getAssociatedEmployerCampaignFromResellerByRole(employerId) {
        return BackendConnector.get(`employer/${employerId}/associatedEmployerCampaignFromResellerByRole`);
    }

    // Generated by AI on 06/03/2026
    static updateEmployer(employerId, employerData) {
        return BackendConnector.put(`employer/${employerId}`, employerData);
    }

    // Generated by AI on 06/03/2026
    static getSpecialtiesUpdateStatus(employerId) {
        return BackendConnector.get(`employer/${employerId}/getSpecialtiesUpdateStatus`);
    }
}
